import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { file, types as sdkTypes } from '../../util/sdkLoader';
import { prepareImageForUpload } from '../../util/imageUpload';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

// ================ Reducer ================ //

// Lado largo máximo del avatar al subirlo: la variante más grande que se pide es de 480 px.
const PROFILE_IMAGE_MAX_DIMENSION = 1024;

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    // Sharetribe cobra la subida por tamaño y el avatar se enseña como mucho a 480 px.
    return prepareImageForUpload(actionPayload.file, { maxDimension: PROFILE_IMAGE_MAX_DIMENSION })
      .then(image => sdk.images.upload({ image }, queryParams))
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

// ================ Ficheros privados ================ //

const { UUID } = sdkTypes;

/**
 * Sube un fichero privado a Sharetribe y devuelve su id como cadena.
 *
 * Son tres pasos, los que define el SDK: registrar el fichero, pedir una URL de
 * subida y subirlo al almacenamiento. Sustituye a la subida a S3 con URL
 * prefirmada, que dejaba el documento en una URL pública y permanente.
 *
 * Subir el fichero NO lo cuelga del perfil: eso lo hace `privateFileAttachments`
 * en `updateProfile`. Hasta entonces es un fichero suelto del usuario.
 */
export const uploadPrivateFile = (fileToUpload, displayName) => (dispatch, getState, sdk) => {
  const { name, mimeType, size } = file.metadata(fileToUpload);

  // `name` es el nombre del fichero en el disco de quien sube, y es lo que la
  // Consola de Sharetribe enseña en "Private files". Sin un nombre nuestro, el
  // operador ve cosas como `736683032ce24f06ba67ce44a2cf0b73.jpg` y no puede
  // distinguir el anverso del reverso.
  return sdk.ownFiles.create({ name: displayName || name, mimeType, size }).then(createRes => {
    const fileId = createRes.data.data.id;

    return sdk.fileUploads.create({ fileId }).then(uploadRes => {
      const { method, url, headers } = uploadRes.data.data.attributes;

      return file.upload({ method, url, headers, file: fileToUpload }).then(() => fileId.uuid);
    });
  });
};

/**
 * URL de lectura de un fichero propio.
 *
 * Es de vida corta by design, así que se pide cada vez que hay que pintarlo y no
 * se guarda en el perfil ni en Redux. Lo que se guarda es el id.
 */
export const privateFileUrl = fileId => (dispatch, getState, sdk) =>
  sdk.ownFileDownloads
    .create({ fileId: new UUID(fileId) })
    .then(res => res.data.data.attributes.url);

export const updateProfile = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
        return response.data.data;
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
};
